import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { borrowerActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import DataGrid, { Column, Paging, Pager } from 'devextreme-react/data-grid';
import { formatDateTime } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { AuditDetailsDialog } from '../../common/SharedComponents';

const EntityAuditHistoryModal = () => {
    const { isOpen, entityId } = useSelector((state) => state.borrower.entityAuditHistoryModal);
    const { dispatch } = store;

    const [auditEntries, set_auditEntries] = useState(null);
    const [auditEntry, set_auditEntry] = useState(null);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const borrowersApi = new BorrowerApi.BorrowerApi(applicationInsights);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        formRow: { marginBottom: '0' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
    };

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = 25;

    const _handleClose = () =>
        dispatch(
            borrowerActions.setEntityAuditHistoryModal({
                entityAuditHistoryModal: { isOpen: false },
            })
        );

    const loadAuditHistory = async (entityId) => {
        try {
            set_loadingMessage('Loading...');
            const response = await borrowersApi.getEntityAuditHistoryList(entityId);
            if (response.statusCode !== 200) {
                return;
            }

            const result = response.result;
            set_auditEntries(result);
        } finally {
            set_loadingMessage('');
        }
    };

    const loadAuditDetails = async (id) => {
        try {
            set_loadingMessage('Loading...');
            const response = await borrowersApi.getEntityAuditHistoryDetail(id);
            if (response.statusCode !== 200) {
                return;
            }
            const result = response.result;
            set_auditEntry(result);
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();
            if (!isOpen || !entityId) {
                return;
            }
            await loadAuditHistory(entityId);
        }

        isOpenChanged();
    }, [isOpen, entityId]);

    const resetState = () => {
        set_auditEntries(null);
        set_auditEntry(null);
    };

    const onRenderDateTime = (e) => {
        if (!e || !e.value) {
            return null;
        }
        return formatDateTime(e.value);
    };

    const showAuditDetail = (event, changeId) => {
        event?.preventDefault();
        event?.stopPropagation();
        loadAuditDetails(changeId);
    };

    const auditActionCellRender = (e) => {
        return (
            <button
                id={'action_' + e.data.id}
                className='gridButton'
                title='View Audit Details...'
                onClick={(event) => showAuditDetail(event, e.data.id)}
            >
                <FontAwesomeIcon icon={faEye} />
            </button>
        );
    };

    const onAuditDetailClose = () => {
        set_auditEntry(null);
    };

    if (!isOpen || !auditEntries) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1000px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>Entity Audit History</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div
                                    className='scrollable-content'
                                    style={{ margin: '10px' }}
                                >
                                    <DataGrid
                                        dataSource={auditEntries}
                                        showBorders={true}
                                        showRowLines={false}
                                        showColumnHeaders={true}
                                        rowAlternationEnabled={true}
                                        columnMinWidth={50}
                                        columnAutoWidth={false}
                                        allowSorting={true}
                                        remoteOperations={false}
                                        allowColumnResizing={true}
                                        onRowDblClick={(e) => showAuditDetail(null, e.data.id)}
                                    >
                                        <Column
                                            dataField='dateAndTime'
                                            caption='Timestamp'
                                            cellRender={onRenderDateTime}
                                            width={160}
                                        />
                                        <Column
                                            dataField='userName'
                                            caption='User'
                                            width={120}
                                            alignment='left'
                                        />
                                        <Column
                                            dataField='notes'
                                            caption='Notes'
                                        />
                                        <Column
                                            alignment='center'
                                            cellRender={auditActionCellRender}
                                            width={70}
                                            allowSorting={false}
                                            allowResizing={false}
                                        />
                                        <Paging defaultPageSize={defaultPageSize} />
                                        <Pager
                                            showPageSizeSelector={allowedPageSizes.length > 1}
                                            allowedPageSizes={allowedPageSizes}
                                            showNavigationButtons={true}
                                            showInfo={true}
                                        />
                                    </DataGrid>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        type='button'
                                        className='button primary'
                                        data-dismiss='modal'
                                        onClick={_handleClose}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
            <AuditDetailsDialog
                isOpen={!!auditEntry}
                auditEntry={auditEntry}
                onClose={onAuditDetailClose}
            />
        </>
    );
};

export default EntityAuditHistoryModal;
